import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Fab, Grid, Paper, Skeleton } from '@mui/material'
import { getViewInstance } from "../actions/viewinstance";
import Header from "./layouts/Header";
import Loading from "./Loading";
import OverallWellnessScore from "./partials/personalreport/OverallWellnessScore";
import KeyAreaGraph from "./partials/personalreport/KeyAreaGraph";
import KeyAreaSectionContainer from "./partials/personalreport/KeyAreaSectionContainer";
//import KeyAreaNavBar from "./partials/personalreport/KeyAreaNavBar";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion"

import { useReactToPrint } from "react-to-print";


import Footer from "./layouts/Footer";
import ResponsiveRectangle from "./partials/skeletons/ResponsiveRectangle";
import RYGGLegend from "./partials/adminreport/RYGGLegend";
import HealthAge from "./partials/personalreport/HealthAge";
import Information from "./partials/personalreport/Information";
import DynamicComponent from "./partials/DynamicComponent";
import HTMLBlock from "./partials/dynamic/HTMLBlock";
import { fetchViewInstances, selectViewInstanceById } from "../reducers/reuseable_vi";


/*
  This style block removes the complex flexbox centering to prevent clipping.
  It now relies on basic resets to ensure content fills the page correctly.
*/
const PrintStyles = () => (
    <style>
        {`
        @media print {
            @page {
                size: 8.3in 11.8in;
                margin: 2mm; /* Defines the printable area */
            }

            /* Add resets to prevent browser defaults from interfering */
            html, body {
                background: #fff !important;
                color: #000 !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100%;
            }

            .no-print {
                display: none !important;
            }

            /* Let the container be a simple block element */
            .print-container {
                width: 90% !important;
                padding: 0 !important;
                background: none !important;
            }
            
            .print-header, .print-footer {
                display: block !important;
                position: static !important;
                box-shadow: none !important;
                background: none !important;
                width: 100%;
            }
            
            .report-legend-container {
                display: block !important;
                margin: 75px 0 50px 0 !important; }   

            /* Ensure the grid takes up full width and has no conflicting margin */
            .print-main-grid {
                margin: 20px 0 0 0 !important;
                padding: 0 !important;
                width: 100% !important;
                box-sizing: border-box !important;
            }
        }
        `}
    </style>
);


const PersonalReport = (props) => {

    const { user: currentUser } = useSelector((state) => state.auth);
    const [callVI, setCallVI] = useState(false)
    useEffect(() => {
        if (window !== undefined) {
            window.scrollTo(0, 0)
        }
    }, [])
    const [searchParams] = useSearchParams();
    const viewInstance = useSelector(state => selectViewInstanceById(state, 'PersonalReport'))
    const viewInstanceLegend = useSelector(state => selectViewInstanceById(state, 'ReportLegend'))
    const settings = viewInstance?.settings || {}
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const motionVariants = {
        hidden: { opacity: 0, x: 0, y: 0 },
        show: {
            opacity: 1, x: 0, y: 0,
            transition: {
                delay: 0.1,
                duration: 0.5,
            }
        }
    }

    const [PrintView, setPrintView] = useState(false);
    const componentRef = useRef(null);
    const onBeforeGetContentResolve = React.useRef(null);
    const reactToPrintContent = React.useCallback(() => {
        return componentRef.current;
    }, [componentRef.current]);

    useEffect(() => { }, [PrintView])

    const handleAfterPrint = React.useCallback(() => {
        setPrintView(false);

    }, []);

    const handleBeforePrint = React.useCallback(() => {
    }, []);

    const handleOnBeforeGetContent = React.useCallback(() => {
        setPrintView(true);
        return new Promise((resolve) => {
            onBeforeGetContentResolve.current = resolve;
            setTimeout(() => {
                setPrintView(true);
                resolve();
            }, 2000);
        });
    }, [setLoading, setPrintView]);

    const handleActualPrint = () => {
        setPrintView(true)
        handlePrint()
    }


    const handlePrint = useReactToPrint({
        content: reactToPrintContent,
        documentTitle: "Personal Report",
        onBeforeGetContent: handleOnBeforeGetContent,
        onBeforePrint: handleBeforePrint,
        onAfterPrint: handleAfterPrint,
        removeAfterPrint: true
    });

    useEffect(() => {
        setLoading(true)
        if ((!viewInstance || !viewInstance.ReportPersonalPage) && !callVI) {
            setCallVI(true)
            dispatch(fetchViewInstances({ authToken: currentUser.AuthToken, VIGuid: searchParams.get("vi"), VIName: "PersonalReport" }))
            setLoading(false)
        }
    }, [window.location.pathname])

    useEffect(() => {
        if ((!viewInstanceLegend || !viewInstanceLegend.LegendsVI)) {
            dispatch(fetchViewInstances({ authToken: currentUser.AuthToken, VIGuid: 'CAFE4E0B-EDD4-466F-A9A9-73833821531F', VIName: "ReportLegend" }))
        }
    }, [window.location.pathname])

    const OrderedSections =
        viewInstance
        && viewInstance.ReportViewInstance
        && viewInstance.ReportViewInstance.ReportPage
        && viewInstance.ReportViewInstance.ReportPage[0].ReportFrame
        && [...viewInstance.ReportViewInstance.ReportPage[0].ReportFrame].sort(function (a, b) { return parseInt(a.Order) - parseInt(b.Order) })
    const VisibleSections = viewInstance && OrderedSections && OrderedSections.filter(x => { return x.Visible === 1 || x.Visible === '1' })

    return (
        <Box ref={componentRef} className="print-container">
            <PrintStyles />
            {/* Remember to apply className="no-print" to the print button INSIDE your Header component */}
            <div className="print-header">
                <Header printFunction={() => { handleActualPrint() }} />
            </div>

            <Grid container direction="row" spacing={2} sx={{ marginTop: '150px', marginLeft: '-12px' }} alignItems="stretch" justifyContent="center" className="print-main-grid">
                <Grid item xs={12}>
                    <Box py={2} className="report-legend-container">
                        <RYGGLegend takeAction={true} showScore={true} PrintView={PrintView} />
                    </Box>
                </Grid>
                {viewInstance && viewInstance.status === 'success' && VisibleSections && VisibleSections.length > 0 ? (
                    VisibleSections.map((x, i) => {
                        return (<DynamicComponent key={i} component={x} settings={settings} PrintView={PrintView} />)
                    })
                ) : (
                    [0, 1, 2, 3].map((x, i) => {
                        return (
                            <Grid key={i} item xs={12} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
                                <Paper elevation={1}>
                                    <Skeleton variant="rectangular" animation="wave" sx={{ paddingTop: "30%" }} height={0} />
                                </Paper>
                            </Grid>
                        )
                    })
                )
                }
            </Grid>

            <div className="print-footer">
                <Footer />
            </div>

        </Box>
    );
};

export default React.forwardRef(PersonalReport);